import { Link, createFileRoute } from '@tanstack/react-router'
import { mobileNormalize, useDebounce } from '@/lib/utils'

import {deletePhoneNumber, getPhonebookList} from '@/queries/phonebook'
import {filterApi} from '@/queries/search'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {LoadingSpinner} from '@/components/ui/loading'
import {Separator} from '@/components/ui/separator'
import {type ColumnDef, type PaginationState} from '@tanstack/react-table'
import {PencilIcon, TrashIcon, UploadIcon, X} from 'lucide-react'
import {useEffect, useState} from 'react'
import {useMutation, useQuery} from '@tanstack/react-query'
import { toast } from 'sonner'
import { DataTable } from '@/components/ui/data-table'
import { exportToExcel, prepareDataForExport } from '@/lib/excelExport/excel'

export const Route = createFileRoute('/phone-book/')({
  component: PhoneBooks,
})

function PhoneBooks() {
  const [deleteDialog, setDeleteDialog] = useState<number>(0)
  const [pagination ,setPagination] = useState<PaginationState>({pageIndex: 0, pageSize: 10})
  const [phoneBooks, setPhoneBooks] = useState([])
  const [searchText, setSearchText] = useState('')
  const [searched, setSearched] = useState<null | Array<any>>(null)
  const debouncedValue = useDebounce(searchText, 1000)
  const [meta, setMeta] = useState<any>()

  const PhoneBookListQuery = useQuery({
    queryFn: () => getPhonebookList(pagination.pageIndex + 1 , pagination.pageSize),
    queryKey: ['getPhoneBooksList', pagination.pageIndex + 1 , pagination.pageSize],
  })

  const excelExportMutation = useMutation({
    mutationFn: () => getPhonebookList(1 , meta.total),
    mutationKey: ['exportAll',],
    onSuccess({data},) {
      const parsed = prepareDataForExport(data.data, 'phoneBook')

      exportToExcel(parsed, 'دفترچه_تلفن.xlsx')
    },
  })

  useEffect(()=> {
    if (PhoneBookListQuery.data?.data) {
      setPhoneBooks(PhoneBookListQuery.data.data.data)
      setMeta(PhoneBookListQuery.data.data.meta)
    }
  },[PhoneBookListQuery.data])

  const deletePhoneBookMutation = useMutation({
    mutationFn: () => deletePhoneNumber(deleteDialog),
    mutationKey: ['deletePhoneNumber', deleteDialog],
    onSuccess() {
      setPhoneBooks((p) => p.filter((s: any) => s.id !== deleteDialog))
      setDeleteDialog(0)
      toast.success('مخاطب با موفقیت حذف شد')
    },
    onError() {
      toast.error('خطایی در حذف مخاطب رخ داده است')
    },
  })

  const searchMutation = useMutation({
    mutationFn: (search:any) => filterApi('phoneBook', search),
    mutationKey: ['search', 'phone', 'phoneBook'],
    onSuccess(data) {
      setSearched(data.data.data)
    
    },
  })

  useEffect(() => {
    if (debouncedValue && debouncedValue !== '') {
      const phone = Number(debouncedValue)
      
      if (!Number.isNaN(phone)) {
        searchMutation.mutate({phone_number: debouncedValue.startsWith('0') ? mobileNormalize(debouncedValue) : debouncedValue})
      } else {
        searchMutation.mutate({name: debouncedValue})
      }
    } else {
      setSearched(null)
    }
  }, [debouncedValue])

  const columns: ColumnDef<{id: number; phone_number: string; name: string}>[] = [
    {
      header: 'شماره تلفن',
      accessorKey: 'phone_number',
      cell(props) {
        return props.row.original.phone_number.replace('98', '0')
      },
    },
    {
      header: 'نام',
      accessorKey: 'name',
    },
    {
      id: 'action',
      header: '',
      cell(props) {
        return (
          <div className='flex gap-2'>
            <Button onClick={() => setDeleteDialog(props.row.original.id)}>
              <TrashIcon />
            </Button>
            <Link to={`/phone-book/$id`} params={{id: String(props.row.original.id) }}>
              <Button variant='secondary'>
                <PencilIcon />
              </Button>
            </Link>
          </div>
        )
      },
    },
  ]

  const handleExcelExport = () => {
    excelExportMutation.mutate()

   
  }

  return (
    <>
      <Dialog open={deleteDialog > 0} onOpenChange={(o) => setDeleteDialog(o == true ? 1 : 0)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>حذف مخاطب</DialogTitle>
          </DialogHeader>
          <div>
            <p>آیا از حذف این مخاطب مطمئن هستید ؟</p>
          </div>
          <DialogFooter>
            <Button variant='secondary' onClick={() => setDeleteDialog(0)}>
              لغو
            </Button>
            <Button
              isLoading={deletePhoneBookMutation.isPending}
              onClick={() => deletePhoneBookMutation.mutate()}
            >
              تایید
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className='flex flex-row justify-between items-center'>
          <CardTitle>دفترچه تلفن</CardTitle>
          <div className='flex gap-2'>
            <Link to='/phone-book/import'>
              <Button className='bg-green-600 hover:bg-green-800 space-x-4 space-x-reverse'>
                <span>ورود شماره از اکسل</span>

                <UploadIcon />
              </Button>
            </Link>
            <Link to='/phone-book/create'>
              <Button>ایجاد مخاطب جدید</Button>
            </Link>
          </div>
        </CardHeader>
        <Separator className='my-2' />
        <CardContent>
          <div className='flex flex-col gap-2 mb-2'>
            <Label>جستجو</Label>
            <div className='flex gap-2'>
              <Input value={searchText} onChange={(e) => setSearchText(e.target.value)} />

              {searchMutation?.isPending ? (
                <LoadingSpinner />
              ) : (
                searched != null && (
                  <Button
                    onClick={() => {
                      setSearched(null)
                      setSearchText('')
                    }}
                  >
                    <X />
                  </Button>
                )
              )}
            </div>
          </div>
          {phoneBooks.length > 0 || (searched && searched?.length > 0) ? <Button isLoading={excelExportMutation.isPaused} onClick={handleExcelExport} className='my-2 bg-green-500 hover:bg-green-700'>
            خروجی اکسل
          </Button>: '' }
          <DataTable
            columns={columns}
            data={searched != null ? searched : phoneBooks}
            pageCount={searchMutation?.data?.data?.meta?.last_page || meta?.last_page}
            pagination={pagination}
            onPaginationChange={setPagination}
          />
        </CardContent>
      </Card>
    </>
  )
}
